import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, HardHat, Users, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface SearchResult {
  type: 'hub' | 'work' | 'professional';
  id: string;
  name: string;
  subtitle?: string;
}

export const GlobalSearch: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchData = async () => {
      if (query.length < 2) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        const searchTerm = `%${query}%`;
        const [hubsRes, worksRes, profRes] = await Promise.all([
          supabase.from('hubs').select('id, name, municipality_code').ilike('name', searchTerm).limit(5),
          supabase.from('works').select('id, name, address').ilike('name', searchTerm).limit(5),
          supabase.from('professionals').select('id, name, email').ilike('name', searchTerm).limit(5),
        ]);

        const combined: SearchResult[] = [
          ...(hubsRes.data || []).map((h) => ({
            type: 'hub' as const,
            id: h.id,
            name: h.name,
            subtitle: `Município: ${h.municipality_code}`,
          })),
          ...(worksRes.data || []).map((w) => ({
            type: 'work' as const,
            id: w.id,
            name: w.name,
            subtitle: w.address || undefined,
          })),
          ...(profRes.data || []).map((p) => ({
            type: 'professional' as const,
            id: p.id,
            name: p.name,
            subtitle: p.email,
          })),
        ];

        setResults(combined);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchData, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  const handleSelect = (result: SearchResult) => {
    setQuery('');
    setIsOpen(false);
    switch (result.type) {
      case 'hub':
        navigate('/hubs');
        break;
      case 'work':
        navigate('/obras');
        break;
      case 'professional':
        navigate('/profissionais');
        break;
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'hub':
        return <MapPin className="w-4 h-4 text-primary" />;
      case 'work':
        return <HardHat className="w-4 h-4 text-warning" />;
      case 'professional':
        return <Users className="w-4 h-4 text-success" />;
      default:
        return <Search className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'hub':
        return 'Hub';
      case 'work':
        return 'Obra';
      case 'professional':
        return 'Profissional';
      default:
        return '';
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar obras, hubs, profissionais..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          className="pl-10 pr-8 bg-muted/50 border-0 focus-visible:bg-background focus-visible:ring-1"
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setResults([]);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {isOpen && (query.length >= 2 || results.length > 0) && (
        <div className="absolute top-full mt-2 w-full bg-popover border rounded-lg shadow-lg z-50 max-h-80 overflow-auto">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground text-sm">
              Buscando...
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground text-sm">
              Nenhum resultado encontrado
            </div>
          ) : (
            <div className="py-2">
              {results.map((result) => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleSelect(result)}
                  className="w-full flex items-center gap-3 px-4 py-2 hover:bg-muted transition-colors text-left"
                >
                  {getIcon(result.type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{result.name}</p>
                    {result.subtitle && (
                      <p className="text-xs text-muted-foreground truncate">{result.subtitle}</p>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">{getTypeLabel(result.type)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
